import type {
  LegacyPlan,
  StakePosition,
  StakingOverview,
  StakingSummary,
  Vault,
  VaultKind,
} from './staking';
import { isLegacy, unrealizedProfit } from './staking';

/**
 * Demo data for the stake screen, used while `/v1/staking` does not exist yet
 * (see `VITE_STAKING_MOCK` in `useStaking`). Figures are the ones from the INCPT
 * prototype, so the screen can be reviewed against the design as-is.
 *
 * Dates are built relative to "now" at call time — a fixture with hard-coded
 * dates would drift into showing elapsed lock-ups and overdue accruals.
 */

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;

export const MOCK_VAULTS: Vault[] = [
  {
    kind: 'liquid',
    apyMin: 20,
    apyMax: 40,
    dailyRate: 0.000719,
    termDays: 365,
    minAmount: 10,
    maxAmount: 20_000,
    limitScope: 'position',
    maxOpenPositions: 1,
    withdrawMode: 'full',
    accrual: 'daily',
    topUpAllowed: true,
    closed: false,
  },
  {
    kind: 'monthly',
    apyMin: 50,
    apyMax: 70,
    dailyRate: 0.00129,
    termDays: 365,
    minAmount: 10,
    maxAmount: 50_000,
    limitScope: 'kind',
    maxOpenPositions: null,
    withdrawMode: 'profit',
    accrual: 'monthly',
    topUpAllowed: false,
    closed: true,
  },
  {
    kind: 'maximum',
    apyMin: 80,
    apyMax: 100,
    dailyRate: 0.00176,
    termDays: 365,
    minAmount: 10,
    maxAmount: 50_000,
    limitScope: 'kind',
    maxOpenPositions: null,
    withdrawMode: 'lock',
    accrual: 'term_end',
    topUpAllowed: false,
    closed: true,
  },
];

export const MOCK_LEGACY_PLANS: LegacyPlan[] = [
  {
    id: 'legacy-liquid',
    monthlyRate: 0.03,
    apy: 42.6,
    withdrawMode: 'full',
    accrual: 'daily',
  },
  {
    id: 'legacy-profit',
    monthlyRate: 0.05,
    apy: 79.6,
    withdrawMode: 'profit',
    accrual: 'monthly',
  },
  {
    id: 'legacy-high',
    monthlyRate: 0.07,
    apy: 125.2,
    withdrawMode: 'lock',
    accrual: 'monthly',
  },
];

/** One prototype position, expressed as offsets from now rather than fixed dates. */
type Seed = {
  id: number;
  vaultKind: VaultKind | null;
  legacyPlanId: string | null;
  invested: number;
  profit: number;
  withdrawn: number;
  openedDaysAgo: number;
  /** Time to the next accrual — whichever unit the plan pays on. */
  nextIn: { days: number } | { hours: number };
  locked: boolean;
};

const SEEDS: Seed[] = [
  {
    id: 47,
    vaultKind: null,
    legacyPlanId: 'legacy-high',
    invested: 30_000,
    profit: 9_500,
    withdrawn: 0,
    openedDaysAgo: 201,
    nextIn: { days: 9 },
    locked: true,
  },
  {
    id: 46,
    vaultKind: null,
    legacyPlanId: 'legacy-profit',
    invested: 40_000,
    profit: 9_500,
    withdrawn: 1_000,
    openedDaysAgo: 216,
    nextIn: { days: 4 },
    locked: false,
  },
  {
    id: 44,
    vaultKind: 'maximum',
    legacyPlanId: null,
    invested: 20_000,
    profit: 6_340,
    withdrawn: 0,
    openedDaysAgo: 182,
    nextIn: { days: 24 },
    locked: true,
  },
  {
    id: 43,
    vaultKind: 'monthly',
    legacyPlanId: null,
    invested: 10_000,
    profit: 3_110,
    withdrawn: 0,
    openedDaysAgo: 166,
    nextIn: { days: 11 },
    locked: false,
  },
  {
    id: 41,
    vaultKind: 'liquid',
    legacyPlanId: null,
    invested: 5_000,
    profit: 234,
    withdrawn: 0,
    openedDaysAgo: 72,
    nextIn: { hours: 6 },
    locked: false,
  },
  {
    id: 38,
    vaultKind: null,
    legacyPlanId: 'legacy-liquid',
    invested: 3_500,
    profit: 300,
    withdrawn: 200,
    openedDaysAgo: 122,
    nextIn: { hours: 21 },
    locked: false,
  },
];

const withdrawModeOf = (s: Seed) =>
  s.vaultKind
    ? MOCK_VAULTS.find((v) => v.kind === s.vaultKind)!.withdrawMode
    : MOCK_LEGACY_PLANS.find((p) => p.id === s.legacyPlanId)!.withdrawMode;

function toPosition(s: Seed, now: number): StakePosition {
  const openedAt = now - s.openedDaysAgo * DAY_MS;
  const nextAccrualAt =
    now + ('days' in s.nextIn ? s.nextIn.days * DAY_MS : s.nextIn.hours * HOUR_MS);
  const current = s.invested + s.profit - s.withdrawn;
  const mode = withdrawModeOf(s);
  const available =
    mode === 'lock' && s.locked ? 0 : mode === 'profit' ? s.profit - s.withdrawn : current;
  // Monthly + Maximum always commit the principal for a year (HTML "Локап тела"),
  // even while Monthly still releases profit. `locked` only gates Maximum payouts.
  const hasTerm = mode !== 'full';

  return {
    id: s.id,
    vaultKind: s.vaultKind,
    legacyPlanId: s.legacyPlanId,
    invested: s.invested,
    profit: s.profit,
    withdrawn: s.withdrawn,
    current,
    available,
    openedAt: new Date(openedAt).toISOString(),
    nextAccrualAt: new Date(nextAccrualAt).toISOString(),
    lockEndsAt: hasTerm ? new Date(openedAt + 365 * DAY_MS).toISOString() : null,
    status: 'active',
  };
}

/** Roll positions up into the hero figure and the per-vault tiles. */
export function summarize(positions: StakePosition[]): StakingSummary {
  const byVault: Record<VaultKind, number> = { liquid: 0, monthly: 0, maximum: 0 };
  let total = 0;
  let invested = 0;
  let profit = 0;
  let legacy = 0;

  for (const p of positions) {
    total += p.current;
    invested += p.invested;
    profit += unrealizedProfit(p);
    if (isLegacy(p)) legacy += p.current;
    else byVault[p.vaultKind as VaultKind] += p.current;
  }

  return { total, invested, profit, byVault, legacy };
}

/** The full mock payload, shaped exactly like a real `/v1/staking` response. */
/** Prototype documents — the real payload serves whatever the admin set. */
const MOCK_LEGAL = {
  terms: 'https://incpt.io/terms/',
  aml: 'https://incpt.io/aml/',
  privacy: 'https://incpt.io/privacy/',
};

export function mockStaking(now = Date.now()): StakingOverview {
  const positions = SEEDS.map((s) => toPosition(s, now));
  return {
    visible: true,
    vaults: MOCK_VAULTS,
    legacyPlans: MOCK_LEGACY_PLANS,
    positions,
    summary: summarize(positions),
    legal: MOCK_LEGAL,
    // A real deposit pays this address; the fixture skips the payment leg.
    treasuryTrx: 'TMockStakingTreasuryAddress000000000',
    fees: { walletUsd: 0, externalUsd: 1 },
    tap: { likes: 0, likeBonus: 0, goal: 100, rewardUsd: 1, lastSeq: 0 },
  };
}
